@if (!readOnly()) {
    <div class="flex justify-end mb-4">
        <p-button type="button" (onClick)="toggleForm()">
            <span pButtonIcon class="material-icons" style="font-size: 1.125rem">add</span>
            <span pButtonLabel>{{ 'bookingV2.advanced.keys.addButton' | translate }}</span>
        </p-button>
    </div>
}

@if (showForm()) {
    <div class="bg-p-surface-50 border border-p-border rounded-lg p-5 mb-4 flex flex-col gap-5">

        <!-- ── Key selection (catalog vs new) ── -->
        <div class="flex flex-col gap-3">
            <u2b-tab-switcher
                class="self-start"
                [tabs]="modeTabs"
                [activeId]="keyMode()"
                (tabSelected)="setMode($event)" />

            @if (keyMode() === 'catalog') {
                <p-iconfield iconPosition="left" styleClass="w-full">
                    <p-inputicon><span class="material-icons text-p-text-muted">search</span></p-inputicon>
                    <input
                        pInputText
                        type="search"
                        class="w-full"
                        [ngModel]="keySearch()"
                        (ngModelChange)="keySearch.set($event)"
                        [placeholder]="('bookingV2.advanced.keys.searchPlaceholder' | translate)"
                        [attr.aria-label]="('bookingV2.advanced.keys.searchPlaceholder' | translate)" />
                </p-iconfield>

                @if (loadingCatalog()) {
                    <div class="flex items-center gap-2 text-sm text-p-text-muted py-2" role="status" aria-live="polite">
                        <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                        {{ 'bookingV2.advanced.keys.loading' | translate }}
                    </div>
                } @else if (filteredCatalog().length === 0) {
                    <p class="text-sm text-p-text-muted text-center py-4 m-0">
                        {{ 'bookingV2.advanced.keys.noResults' | translate }}
                    </p>
                } @else {
                    <div class="flex flex-col gap-1.5 max-h-56 overflow-y-auto border border-p-border rounded-lg bg-p-content-bg p-2"
                         role="listbox" [attr.aria-label]="('bookingV2.advanced.keys.searchPlaceholder' | translate)">
                        @for (entry of filteredCatalog(); track entry.id) {
                            @let selected = isCatalogSelected(entry);
                            <button
                                type="button"
                                role="option"
                                [attr.aria-selected]="selected"
                                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                                [class.border-p-primary]="selected"
                                [class.border-transparent]="!selected"
                                [class.hover:bg-p-surface-100]="!selected"
                                [style.background]="selected ? 'color-mix(in srgb, var(--p-primary-color) 8%, transparent)' : null"
                                (click)="selectCatalogEntry(entry)">
                                <span class="material-icons flex-shrink-0" [class.text-p-primary]="selected" [class.text-p-text-muted]="!selected" aria-hidden="true">vpn_key</span>
                                <div class="flex-1 min-w-0">
                                    <p class="font-medium text-p-text truncate m-0">{{ entry.name }}</p>
                                    <p class="text-xs text-p-text-muted m-0 mt-0.5">
                                        {{ entry.typeLabel }}
                                        @if (entry.identifier) { · {{ entry.identifier }} }
                                        @if (entry.depositAmount !== undefined) { · {{ 'bookingV2.advanced.keys.depositPrefix' | translate }} {{ entry.depositAmount }} € }
                                    </p>
                                </div>
                                <span class="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium"
                                      [class.bg-sky-100]="entry.uniqueness === 'unique'"
                                      [class.text-sky-800]="entry.uniqueness === 'unique'"
                                      [class.bg-teal-100]="entry.uniqueness === 'duplicated'"
                                      [class.text-teal-800]="entry.uniqueness === 'duplicated'">
                                    @if (entry.uniqueness === 'unique') {
                                        {{ 'bookingV2.advanced.keys.unique' | translate }}
                                    } @else if (entry.totalCopies !== undefined) {
                                        {{ 'bookingV2.advanced.keys.duplicatedWithCount' | translate: { count: entry.totalCopies } }}
                                    } @else {
                                        {{ 'bookingV2.advanced.keys.duplicated' | translate }}
                                    }
                                </span>
                                @if (selected) {
                                    <span class="material-icons text-p-primary flex-shrink-0" aria-hidden="true">check_circle</span>
                                }
                            </button>
                        }
                    </div>
                }

                @if (catalogRef()) {
                    <p class="text-sm font-medium text-p-text m-0">
                        {{ 'bookingV2.advanced.keys.selectedPrefix' | translate }}
                        <span class="text-p-primary">{{ newDescription() }}</span>
                    </p>
                }
            }

            @if (keyMode() === 'new') {
                <div class="flex flex-col gap-3">
                    <div class="flex flex-col gap-1">
                        <label for="key-type" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.keys.typeLabel' | translate }}
                        </label>
                        <p-select inputId="key-type" styleClass="w-full" appendTo="body" optionValue="value"
                            [options]="keyTypeOptions" [ngModel]="newType()" (ngModelChange)="newType.set($event)">
                            <ng-template #selectedItem let-opt>@if (opt) { {{ opt.labelKey | translate }} }</ng-template>
                            <ng-template let-opt #item>{{ opt.labelKey | translate }}</ng-template>
                            <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                        </p-select>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label for="key-desc" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.keys.descriptionLabel' | translate }}
                            <span class="text-red-500" aria-hidden="true">*</span>
                        </label>
                        <input pInputText id="key-desc" type="text" class="w-full"
                            [placeholder]="('bookingV2.advanced.keys.descriptionPlaceholder' | translate)"
                            [ngModel]="newDescription()" (ngModelChange)="newDescription.set($event)" />
                    </div>

                    @if (showIdentifierField()) {
                        <div class="flex flex-col gap-1">
                            <label for="key-id" class="text-sm font-medium text-p-text">
                                {{ 'bookingV2.advanced.keys.identifierLabel' | translate }}
                            </label>
                            <input pInputText id="key-id" type="text" class="w-full"
                                [placeholder]="('bookingV2.advanced.keys.identifierPlaceholder' | translate)"
                                [ngModel]="newIdentifier()" (ngModelChange)="newIdentifier.set($event)" />
                        </div>
                    }

                    <div class="flex flex-col gap-1.5">
                        <p class="text-sm font-medium text-p-text m-0">{{ 'bookingV2.advanced.keys.uniquenessLabel' | translate }}</p>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup"
                             [attr.aria-label]="('bookingV2.advanced.keys.uniquenessLabel' | translate)">
                            <label class="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors"
                                   [class.border-p-primary]="newUniqueness() === 'unique'"
                                   [class.border-p-border]="newUniqueness() !== 'unique'"
                                   [style.background]="newUniqueness() === 'unique' ? 'color-mix(in srgb, var(--p-primary-color) 6%, transparent)' : null">
                                <p-radiobutton inputId="key-uniq-unique" name="key-uniqueness" value="unique"
                                    [ngModel]="newUniqueness()" (ngModelChange)="newUniqueness.set($event)" />
                                <span>
                                    <span class="block text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.uniqueOption.label' | translate }}</span>
                                    <span class="block text-xs text-p-text-muted mt-0.5">{{ 'bookingV2.advanced.keys.uniqueOption.desc' | translate }}</span>
                                </span>
                            </label>
                            <label class="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors"
                                   [class.border-p-primary]="newUniqueness() === 'duplicated'"
                                   [class.border-p-border]="newUniqueness() !== 'duplicated'"
                                   [style.background]="newUniqueness() === 'duplicated' ? 'color-mix(in srgb, var(--p-primary-color) 6%, transparent)' : null">
                                <p-radiobutton inputId="key-uniq-dup" name="key-uniqueness" value="duplicated"
                                    [ngModel]="newUniqueness()" (ngModelChange)="newUniqueness.set($event)" />
                                <span>
                                    <span class="block text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.duplicatedOption.label' | translate }}</span>
                                    <span class="block text-xs text-p-text-muted mt-0.5">{{ 'bookingV2.advanced.keys.duplicatedOption.desc' | translate }}</span>
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            }
        </div>

        <!-- ── Borrower: person and/or boat ── -->
        <div class="flex flex-col gap-3 pt-4 border-t border-p-border">
            <div>
                <p class="text-sm font-semibold text-p-text m-0">{{ 'bookingV2.advanced.keys.borrowerHeading' | translate }}</p>
                <p class="text-xs text-p-text-muted m-0 mt-0.5">{{ 'bookingV2.advanced.keys.borrowerHint' | translate }}</p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <!-- Person -->
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.personLabel' | translate }}</label>
                    @if (selectedPerson(); as person) {
                        <div class="flex items-center justify-between gap-2 rounded-lg border border-p-border bg-p-content-bg px-3 py-2">
                            <span class="flex items-center gap-2 min-w-0">
                                <span class="material-icons text-p-text-muted" aria-hidden="true">{{ person.kind === 'company' ? 'business' : 'person' }}</span>
                                <span class="truncate text-sm text-p-text">{{ person.displayName }}</span>
                            </span>
                            <u2b-icon-button icon="close"
                                [ariaLabel]="('bookingV2.advanced.keys.removePersonAria' | translate)"
                                (clicked)="removePerson()" />
                        </div>
                    } @else {
                        <p-iconfield iconPosition="left" styleClass="w-full">
                            <p-inputicon><span class="material-icons text-p-text-muted">search</span></p-inputicon>
                            <input pInputText type="search" class="w-full"
                                [ngModel]="personQuery()" (ngModelChange)="onPersonQueryChange($event)"
                                [placeholder]="('bookingV2.advanced.keys.personSearchPlaceholder' | translate)"
                                [attr.aria-label]="('bookingV2.advanced.keys.personSearchPlaceholder' | translate)" />
                        </p-iconfield>
                        @if (loadingPersonSearch()) {
                            <div class="flex items-center gap-2 text-xs text-p-text-muted py-1" role="status" aria-live="polite">
                                <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                                {{ 'bookingV2.advanced.keys.searching' | translate }}
                            </div>
                        } @else if (personQueryTooShort()) {
                            <p class="text-xs text-p-text-muted m-0">{{ 'bookingV2.advanced.keys.searchHint' | translate }}</p>
                        } @else if (personShowNoResults()) {
                            <p class="text-xs text-p-text-muted m-0">{{ 'bookingV2.advanced.keys.noPersonResults' | translate }}</p>
                        } @else {
                            <div class="flex flex-col gap-1 max-h-40 overflow-y-auto border border-p-border rounded-lg bg-p-content-bg p-2"
                                 role="listbox" [attr.aria-label]="('bookingV2.advanced.keys.personSearchPlaceholder' | translate)">
                                @for (option of personSearchResult(); track option.kind + '-' + option.id) {
                                    <button type="button" role="option" [attr.aria-selected]="false"
                                        class="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm border border-transparent transition-colors hover:bg-p-surface-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                                        (click)="selectPerson(option)">
                                        <span class="material-icons text-p-text-muted flex-shrink-0" aria-hidden="true">{{ option.kind === 'company' ? 'business' : 'person' }}</span>
                                        <span class="flex-1 min-w-0">
                                            <span class="block truncate text-p-text">{{ option.displayName }}</span>
                                            @if (option.address) { <span class="block truncate text-xs text-p-text-muted">{{ option.address }}</span> }
                                        </span>
                                    </button>
                                }
                            </div>
                        }
                    }
                </div>

                <!-- Boat -->
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.boatLabel' | translate }}</label>
                    @if (selectedBoat(); as boat) {
                        <div class="flex items-center justify-between gap-2 rounded-lg border border-p-border bg-p-content-bg px-3 py-2">
                            <span class="flex items-center gap-2 min-w-0">
                                <span class="material-icons text-p-text-muted" aria-hidden="true">directions_boat</span>
                                <span class="truncate text-sm text-p-text">{{ boat.label }}</span>
                            </span>
                            <u2b-icon-button icon="close"
                                [ariaLabel]="('bookingV2.advanced.keys.removeBoatAria' | translate)"
                                (clicked)="removeBoat()" />
                        </div>
                    } @else {
                        <p-iconfield iconPosition="left" styleClass="w-full">
                            <p-inputicon><span class="material-icons text-p-text-muted">search</span></p-inputicon>
                            <input pInputText type="search" class="w-full"
                                [ngModel]="boatQuery()" (ngModelChange)="onBoatQueryChange($event)"
                                [placeholder]="('bookingV2.advanced.keys.boatSearchPlaceholder' | translate)"
                                [attr.aria-label]="('bookingV2.advanced.keys.boatSearchPlaceholder' | translate)" />
                        </p-iconfield>
                        @if (loadingBoatSearch()) {
                            <div class="flex items-center gap-2 text-xs text-p-text-muted py-1" role="status" aria-live="polite">
                                <p-progressspinner styleClass="berth-loading-spinner" strokeWidth="3" />
                                {{ 'bookingV2.advanced.keys.searching' | translate }}
                            </div>
                        } @else if (boatQueryTooShort()) {
                            <p class="text-xs text-p-text-muted m-0">{{ 'bookingV2.advanced.keys.searchHint' | translate }}</p>
                        } @else if (boatShowNoResults()) {
                            <p class="text-xs text-p-text-muted m-0">{{ 'bookingV2.advanced.keys.noBoatResults' | translate }}</p>
                        } @else {
                            <div class="flex flex-col gap-1 max-h-40 overflow-y-auto border border-p-border rounded-lg bg-p-content-bg p-2"
                                 role="listbox" [attr.aria-label]="('bookingV2.advanced.keys.boatSearchPlaceholder' | translate)">
                                @for (option of boatSearchResult(); track option.id) {
                                    <button type="button" role="option" [attr.aria-selected]="false"
                                        class="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm border border-transparent transition-colors hover:bg-p-surface-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-p-primary"
                                        (click)="selectBoat(option)">
                                        <span class="material-icons text-p-text-muted flex-shrink-0" aria-hidden="true">directions_boat</span>
                                        <span class="flex-1 min-w-0 truncate text-p-text">{{ option.label }}</span>
                                    </button>
                                }
                            </div>
                        }
                    }
                </div>
            </div>

            @if (companyBorrowerNeedsBoat()) {
                <small class="text-red-500">{{ 'bookingV2.advanced.keys.companyBorrowerRequiresBoat' | translate }}</small>
            }
        </div>

        <!-- ── Lending details ── -->
        <div class="flex flex-col gap-3 pt-4 border-t border-p-border">
            <p class="text-sm font-semibold text-p-text m-0">{{ 'bookingV2.advanced.keys.detailsHeading' | translate }}</p>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div class="flex flex-col gap-1">
                    <label for="key-number" class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.keyNumberLabel' | translate }}</label>
                    <input pInputText id="key-number" type="text" class="w-full"
                        [placeholder]="('bookingV2.advanced.keys.keyNumberPlaceholder' | translate)"
                        [ngModel]="keyNumber()" (ngModelChange)="keyNumber.set($event)" />
                </div>

                <div class="flex flex-col gap-1">
                    <label for="key-from" class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.issuedOnLabel' | translate }}</label>
                    <p-datepicker inputId="key-from" u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" [showButtonBar]="true"
                        appendTo="body" styleClass="w-full"
                        [placeholder]="('bookingV2.advanced.keys.datePlaceholder' | translate)"
                        [ngModel]="fromDate()" (ngModelChange)="fromDate.set($event)">
                        <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                        <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                        <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                    </p-datepicker>
                </div>

                <div class="flex flex-col gap-1">
                    <label for="key-to" class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.returnedOnLabel' | translate }}</label>
                    <p-datepicker inputId="key-to" u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" [showButtonBar]="true"
                        appendTo="body" styleClass="w-full"
                        [placeholder]="('bookingV2.advanced.keys.datePlaceholder' | translate)"
                        [ngModel]="toDate()" (ngModelChange)="toDate.set($event)">
                        <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                        <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                        <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                    </p-datepicker>
                </div>
            </div>
        </div>

        <!-- ── Deposit (Pfand): picked in "new" mode, inherited (read-only) in catalog mode ── -->
        @if (keyMode() === 'new' || depositProductId() !== null) {
            <div class="flex flex-col gap-2 pt-4 border-t border-p-border">
                @if (keyMode() === 'new') {
                    <label for="key-deposit-product" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.advanced.keys.depositProductLabel' | translate }}
                    </label>
                    @if (depositProducts().length === 0) {
                        <span class="text-xs text-p-text-muted">{{ 'bookingV2.advanced.keys.depositNoProducts' | translate }}</span>
                    } @else {
                        <p-select inputId="key-deposit-product" styleClass="w-full sm:w-96" appendTo="body"
                            [showClear]="true" optionValue="id" optionLabel="label" [options]="depositProducts()"
                            [ngModel]="depositProductId()" (ngModelChange)="onDepositProductChange($event)"
                            [placeholder]="('bookingV2.advanced.keys.depositProductPlaceholder' | translate)">
                            <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                            <ng-template #clearicon><span class="material-icons">close</span></ng-template>
                        </p-select>
                    }
                } @else {
                    <p class="text-sm text-p-text m-0">
                        <span class="font-medium">{{ 'bookingV2.advanced.keys.depositPrefix' | translate }}: {{ depositAmount() }} €</span>
                        <span class="text-xs text-p-text-muted ml-1">({{ 'bookingV2.advanced.keys.depositFromProductHint' | translate }})</span>
                    </p>
                }

                @if (depositProductId() !== null) {
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                    <div class="flex flex-col gap-1">
                        <label for="key-deposit-paid" class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.depositPaidOnLabel' | translate }}</label>
                        <p-datepicker inputId="key-deposit-paid" u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" [showButtonBar]="true"
                            appendTo="body" styleClass="w-full"
                            [placeholder]="('bookingV2.advanced.keys.datePlaceholder' | translate)"
                            [ngModel]="keyFeePayedOn()" (ngModelChange)="keyFeePayedOn.set($event)">
                            <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                            <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                            <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                        </p-datepicker>
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="key-deposit-refunded" class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.depositRefundedOnLabel' | translate }}</label>
                        <p-datepicker inputId="key-deposit-refunded" u2bDateMask dateFormat="dd.mm.yy" [showIcon]="true" [showButtonBar]="true"
                            appendTo="body" styleClass="w-full"
                            [placeholder]="('bookingV2.advanced.keys.datePlaceholder' | translate)"
                            [ngModel]="keyFeePayedBackOn()" (ngModelChange)="keyFeePayedBackOn.set($event)">
                            <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                            <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                            <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                        </p-datepicker>
                    </div>
                </div>
            }
            </div>
        }

        <!-- ── Note ── -->
        <div class="flex flex-col gap-1 pt-4 border-t border-p-border">
            <label for="key-note" class="text-sm font-medium text-p-text">{{ 'bookingV2.advanced.keys.noteLabel' | translate }}</label>
            <textarea pTextarea id="key-note" rows="3" class="w-full"
                [placeholder]="('bookingV2.advanced.keys.notePlaceholder' | translate)"
                [ngModel]="note()" (ngModelChange)="note.set($event)"></textarea>
        </div>

        <div class="flex gap-2 justify-end">
            <p-button type="button" [disabled]="!canAdd()" [label]="'common.save' | translate" (onClick)="add()" />
            <p-button type="button" severity="secondary" [text]="true" [label]="'common.cancel' | translate" (onClick)="closeForm()" />
        </div>
    </div>
}

<!-- ── Captured lendings ── -->
<div class="flex flex-col gap-2">
    <div class="flex flex-col gap-3">
        @for (key of keys(); track key.uuid) {
            <u2b-indicator-card accent="slate" icon="vpn_key">
                <div class="min-w-0 flex flex-col gap-0.5">
                    <div class="flex items-center gap-2 flex-wrap">
                        <p class="font-semibold text-p-text m-0">{{ key.keyName }}</p>
                        <span class="px-2 py-0.5 rounded-full text-xs font-medium"
                              [class.bg-sky-100]="key.uniqueness === 'unique'"
                              [class.text-sky-800]="key.uniqueness === 'unique'"
                              [class.bg-teal-100]="key.uniqueness === 'duplicated'"
                              [class.text-teal-800]="key.uniqueness === 'duplicated'">
                            {{ (key.uniqueness === 'unique'
                                ? 'bookingV2.advanced.keys.unique'
                                : 'bookingV2.advanced.keys.duplicated') | translate }}
                        </span>
                    </div>

                    <p class="text-sm text-p-text-muted m-0">
                        @if (key.keyTypeLabel) {
                            {{ key.keyTypeLabel }}
                        } @else if (key.keyType) {
                            {{ keyTypeLabelKeys[key.keyType] | translate }}
                        }
                        @if (key.keyIdentifier) { · {{ key.keyIdentifier }} }
                        @if (key.keyNumber) { · {{ 'bookingV2.advanced.keys.keyNumberLabel' | translate }} {{ key.keyNumber }} }
                    </p>

                    <!-- Borrower -->
                    <p class="text-sm text-p-text m-0 flex items-center gap-3 flex-wrap">
                        @if (key.personLabel) {
                            <span class="flex items-center gap-1">
                                <span class="material-icons text-base text-p-text-muted" aria-hidden="true">{{ key.personKind === 'company' ? 'business' : 'person' }}</span>
                                {{ key.personLabel }}
                            </span>
                        }
                        @if (key.boatLabel) {
                            <span class="flex items-center gap-1">
                                <span class="material-icons text-base text-p-text-muted" aria-hidden="true">directions_boat</span>
                                {{ key.boatLabel }}
                            </span>
                        }
                    </p>

                    <!-- Lending window -->
                    @if (key.from || key.to) {
                        <p class="text-xs text-p-text-muted m-0">
                            {{ 'bookingV2.advanced.keys.issuedOnLabel' | translate }}: {{ key.from ? (key.from | date : 'dd.MM.yyyy') : '—' }}
                            · {{ 'bookingV2.advanced.keys.returnedOnLabel' | translate }}: {{ key.to ? (key.to | date : 'dd.MM.yyyy') : '—' }}
                        </p>
                    }

                    <!-- Deposit -->
                    @if (key.depositAmount !== null) {
                        <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                            <span class="text-sm"
                                  [class.line-through]="key.keyFeePayedBackOn"
                                  [class.text-p-text-muted]="key.keyFeePayedBackOn"
                                  [class.text-p-primary]="!key.keyFeePayedBackOn"
                                  [class.font-medium]="!key.keyFeePayedBackOn">
                                {{ 'bookingV2.advanced.keys.depositPrefix' | translate }}: {{ key.depositAmount }} €
                            </span>
                            @if (key.keyFeePayedOn) {
                                <span class="text-xs text-p-text-muted">
                                    {{ 'bookingV2.advanced.keys.depositPaidOnLabel' | translate }}: {{ key.keyFeePayedOn | date : 'dd.MM.yyyy' }}
                                </span>
                            }
                            @if (key.keyFeePayedBackOn) {
                                <span class="text-xs bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full">
                                    {{ 'bookingV2.advanced.keys.depositReturned' | translate }}
                                </span>
                            }
                        </div>
                    }

                    @if (key.note) {
                        <p class="text-xs text-p-text-muted m-0 mt-0.5 italic">{{ key.note }}</p>
                    }
                </div>
                @if (!readOnly()) {
                    <u2b-icon-button
                        u2bIndicatorAction
                        icon="close"
                        [ariaLabel]="('bookingV2.advanced.keys.removeAria' | translate)"
                        (clicked)="remove(key.uuid)" />
                }
            </u2b-indicator-card>
        }
    </div>
    @if (keys().length === 0 && !showForm()) {
        <p class="text-center text-p-text-muted py-6 text-sm m-0">
            {{ 'bookingV2.advanced.keys.empty' | translate }}
        </p>
    }
</div>
